import {
  InvestigationStatus,
  useMarkDiscrepancyAsInvestigatedMutation,
} from '@/api/ariadne/__generated_types__/apollo';
import * as userSelectors from '@/user/user-selectors';
import { AgGridReact as AgGridReactType } from '@ag-grid-community/react/lib/agGridReact';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

type Props = {
  gridApi: React.RefObject<AgGridReactType>;
};

export function useMarkTaxItemAsInvestigated({ gridApi }: Props) {
  const regionId: string = useSelector(userSelectors.currentCity);

  const [markDiscrepancyAsInvestigated] =
    useMarkDiscrepancyAsInvestigatedMutation();

  const updateCache = useCallback(
    (taxItemId: string, newInvestigationStatus: InvestigationStatus) => {
      //  Update the ag-grid cache to the new investigation status
      const rowNode = gridApi.current?.api.getRowNode(taxItemId);

      if (rowNode) {
        // For optimistic update, we need to manually update the row node data
        // This data will be synced with the server when new data is fetched from mutation
        // There is no clear way to disable cache in ag-grid, https://www.ag-grid.com/javascript-data-grid/server-side-model-configuration/#server-side-cache
        rowNode.setData({
          ...rowNode.data,
          discrepancyInvestigated: newInvestigationStatus,
        });
      }
    },
    [gridApi]
  );

  const markTaxItemAsInvestigated = useCallback(
    async (taxItemId: string) => {
      try {
        // Optimistically update the cache
        updateCache(taxItemId, InvestigationStatus.Investigated);

        await markDiscrepancyAsInvestigated({
          variables: { taxItemId, regionId },
          optimisticResponse: {
            markDiscrepancyAsInvestigated: true,
          },
          update: (_, { data }) => {
            // Case where mutation fails and doesn't return data or updated discrepancyInvestigated status
            if (!data?.markDiscrepancyAsInvestigated) {
              // revert the cache to not investigated
              updateCache(taxItemId, InvestigationStatus.NotInvestigated);
              return;
            }
          },
        });
      } catch (error) {
        // If there's an error, revert the cache to not investigated
        updateCache(taxItemId, InvestigationStatus.NotInvestigated);
        throw error; // Re-throw to let the component handle the error display
      }
    },
    [markDiscrepancyAsInvestigated, updateCache, regionId]
  );

  return { markTaxItemAsInvestigated };
}
